import {
  FiberManualRecord as FiberManualRecordIcon,
  MoreVert as MoreVertIcon,
  Public as PublicIcon,
  Share as ShareIcon,
} from '@mui/icons-material';
import {
  Avatar,
  Badge,
  Box,
  Card,
  CardContent,
  CardHeader,
  CardMedia,
  Divider,
  Stack,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CommonHelper } from 'src/utils/commonHelper';
import GbusinessOfferIcon from '../../../../assets/gbusiness-offer-icon.svg';
import GbusinessVerifyUser from '../../../../assets/gbusiness-verify-user.svg';
import PreviewMediaView from '../preview-media-view.component';

const HoverLink = styled('a')(({ theme }) => ({
  textDecoration: 'none',
  color: 'inherit',
  '&:hover': {
    textDecoration: 'underline',
    color: theme.palette.primary.main,
  },
}));

const offerFormatDate = (dateString) => {
  const date = new Date(dateString);
  const options = { day: 'numeric', month: 'short' };
  return new Intl.DateTimeFormat('en-US', options).format(date);
};

const GBusinessOfferPreview = ({ data }) => {
  const { t: translate } = useTranslation();
  let mediaFiles = CommonHelper.ProcessMediaUrl(data?.media_url)?.media_data;

  const theme = useTheme();
  const respwidth = useMediaQuery(theme.breakpoints.up('xl'));
  const ConnectedProfiles = data.connected_profile_details;
  const offerFormattedStartDate = offerFormatDate(data.start_date);
  const offerFormattedEndDate = offerFormatDate(data.end_date);
  const [showOffers, setShowOffers] = useState(false);
  const [showFullDescription, setShowFullDescription] = useState(false);
  const handleToggleDescription = () => {
    setShowFullDescription(!showFullDescription);
  };

  const cardOffers = (data) => {
    return (
      <>
        <Card
          sx={{
            textAlign: 'center',
            boxShadow: 'none',
            p: 1.5,
            flexGrow: 1,
            bgcolor: 'background.neutral',
            border: 'dashed',
            borderColor: theme.palette.preview.comments.gbusiness,
            mt: 2,
            mb: 2,
          }}
        >
          <Typography sx={{ mb: 0.5 }}>{translate('preview.show_this_code_at_shop')}</Typography>
          <Typography
            variant="h4"
            sx={{
              fontWeight: 700,
              mb: 0.5,
            }}
          >
            {data.offer_code}
          </Typography>
          <Typography sx={{ fontSize: '0.8rem' }}>
            {translate('preview.valid')} {offerFormattedStartDate} - {offerFormattedEndDate}
          </Typography>
        </Card>
        <HoverLink href={data.url} target="_blank">
          <Typography
            sx={{
              textDecoration: 'underline',
              cursor: 'pointer',
              color: theme.palette.primary.main,
              mb: 2,
            }}
          >
            {translate('preview.redeem_online')}
          </Typography>
        </HoverLink>
        <Divider />
        <Typography sx={{ fontSize: '0.8rem', mt: 1 }}>{data.terms_conditions}</Typography>
      </>
    );
  };

  const ProfileTime = () => {
    return (
      <>
        <Stack direction="row" justifyContent="flex-start" alignItems="flex-start" sx={{ gap: '0.3rem' }}>
          <Typography variant="span">{translate('preview.just_now')}</Typography>
          <FiberManualRecordIcon sx={{ fontSize: 5, mt: '0.6rem' }} />
          <PublicIcon sx={{ fontSize: 15, mt: '0.3rem' }} />
        </Stack>
      </>
    );
  };

  return (
    <>
      <Stack container justifyContent="center" alignItems="center">
        <Card
          sx={{
            maxWidth: 500,
            minWidth: respwidth ? 450 : null,
            boxShadow: 3,
            borderRadius: 2,
            border: 'none',
            borderColor: theme.palette.preview.postcard.color,
          }}
        >
          <CardHeader
            avatar={
              <Badge
                overlap="circular"
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                badgeContent={
                  <img
                    src={GbusinessVerifyUser}
                    sx={{
                      width: 20,
                      height: 20,
                      overflow: 'hidden',
                      background: 'white',
                      color: theme.palette.preview.postcard.gbverifyicon,
                      ml: 1,
                      mb: 1,
                    }}
                  />
                }
              >
                <Avatar
                  src={
                    ConnectedProfiles.connected_profile_image
                      ? ConnectedProfiles.connected_profile_image
                      : ConnectedProfiles.connected_profile_name || ConnectedProfiles.connected_profile_id
                  }
                  sx={{
                    alignItems: 'center',
                    justifyContent: 'center',
                    textAlign: 'center',
                    height: 40,
                    width: 40,
                    bgcolor: theme.palette.primary.main,
                  }}
                  style={{ color: 'white' }}
                />
              </Badge>
            }
            title={ConnectedProfiles?.connected_profile_name}
            sx={{ fontSize: { xl: 15, lg: 13, md: 11, sm: 9, xs: 7 }, mb: 1 }}
            subheader={<ProfileTime />}
            action={<MoreVertIcon sx={{ mt: 1 }} />}
          />
          {mediaFiles && (
            <CardMedia>
              <PreviewMediaView data={data} />
            </CardMedia>
          )}
          <CardContent
            disableSpacing
            sx={{
              display: 'flex',
              flexDirection: 'column',
              height: '100%',
            }}
          >
            <Box
              sx={{
                marginTop: 'auto',
                display: 'flex',
                justifyContent: 'flex-end',
                alignItems: 'flex-end',
              }}
            >
              <img src={GbusinessOfferIcon} alt="Business Offer Icon" style={{ width: 25, height: 25 }} />
            </Box>
            <Typography sx={{ fontSize: '1.5rem', mt: '-2rem' }}>{data.title}</Typography>
            <Typography sx={{ fontSize: '0.8rem', mb: 2 }}>
              {translate('preview.valid')} {offerFormattedStartDate} - {offerFormattedEndDate}
            </Typography>
            <Typography
              noWrap={!showFullDescription}
              sx={{
                display: '-webkit-box',
                WebkitBoxOrient: 'vertical',
                WebkitLineClamp: showFullDescription ? 'initial' : 2,
                overflow: 'hidden',
                WebkitBoxDecorationBreak: 'clone',
                whiteSpace: 'pre-line',
                textOverflow: 'ellipsis',
                fontSize: '0.875rem',
              }}
            >
              {CommonHelper.ParseDescription(data.description)}
            </Typography>
            {!showFullDescription && data.description.length > 100 && (
              <Typography
                variant="body1"
                sx={{
                  fontWeight: 700,
                  textDecoration: 'underline',
                  cursor: 'pointer',
                  fontSize: '0.875rem',
                  color: theme.palette.primary.main,
                }}
                onClick={handleToggleDescription}
              >
                {translate('preview.show_more')}
              </Typography>
            )}
            {showFullDescription && (
              <Typography
                variant="body1"
                sx={{
                  fontWeight: 700,
                  textDecoration: 'underline',
                  cursor: 'pointer',
                  fontSize: '0.875rem',
                  color: theme.palette.primary.main,
                }}
                onClick={handleToggleDescription}
              >
                {translate('preview.show_less')}
              </Typography>
            )}
            {!showOffers && (
              <Typography
                sx={{
                  textDecoration: 'underline',
                  cursor: 'pointer',
                  color: theme.palette.primary.main,
                  mb: 2,
                }}
                onClick={() => setShowOffers(true)}
              >
                {translate('preview.view_offers')}
              </Typography>
            )}
            {showOffers && cardOffers(data)}

            <Box
              sx={{
                marginTop: 'auto',
                display: 'flex',
                justifyContent: 'flex-end',
                alignItems: 'flex-end',
              }}
            >
              <ShareIcon />
            </Box>
          </CardContent>
        </Card>
      </Stack>
    </>
  );
};

export default GBusinessOfferPreview;
